import React, { useState, useRef, useEffect } from "react";

/**
 * A lightweight, dependency-free multi-select tag picker with search.
 * Styled with Tailwind to match the Game Goblin brand.
 */
export default function TagMultiSelect({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = "Search or select tags...",
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const filtered = options.filter(
    (opt) =>
      !value.includes(opt) &&
      opt.toLowerCase().includes((inputValue || "").toLowerCase())
  );

  const toggleTag = (tag) => {
    if (value.includes(tag)) {
      onChange(value.filter((t) => t !== tag));
    } else {
      onChange([...value, tag]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <p className="text-brand-purple text-[13px] font-bold mb-1">{label}</p>
      )}
      <div
        onClick={() => setOpen(true)}
        className="w-full min-h-9 rounded-lg px-2 py-1.5 text-sm bg-white/5 border border-brand-gold/30 focus-within:border-brand-gold transition-colors duration-150 flex flex-wrap gap-1.5 cursor-text"
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-brand-purple/30 border border-brand-purple text-brand-gold text-xs px-2 py-0.5"
          >
            {tag}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleTag(tag);
              }}
              className="hover:text-white"
              aria-label={`Remove ${tag}`}
            >
              ✕
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setInputValue(e.target.value);
            setOpen(true);
          }}
          placeholder={loading ? "Loading..." : value.length ? "" : placeholder}
          className="flex-1 min-w-[80px] bg-transparent outline-none text-white placeholder:text-white/40 text-sm py-0.5"
        />
      </div>
      {open && !loading && filtered.length > 0 && (
        <div className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-brand-gold bg-surface-light/98 backdrop-blur-md shadow-card animate-fadeIn">
          {filtered.map((opt) => (
            <div
              key={opt}
              onMouseDown={() => {
                toggleTag(opt);
                setInputValue("");
              }}
              className="px-3 py-2 text-sm cursor-pointer text-white border-b border-brand-gold/10 last:border-b-0 hover:bg-brand-purple/20 transition-colors"
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
